// Nachweise: customers report where a licensed image was published (link, book, anything else, optionally with a file).
import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { ApiError } from '../api'
import type { Order, Proof, ProofKind } from '../shop'
import { shop } from '../shop'

const KINDS: { kind: ProofKind; label: string }[] = [
  { kind: 'url', label: 'Online (Link)' },
  { kind: 'book', label: 'Buch' },
  { kind: 'other', label: 'Sonstiges' },
]
const ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp,image/gif,image/tiff'
const MAX_MB = 20
const EMPTY = { url: '', title: '', author: '', isbn: '', text: '' }

const kb = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB` : `${Math.max(1, Math.round(n / 1024))} KB`)

function isbnHyphens(s: string) {
  return s.length === 13 ? `${s.slice(0, 3)}-${s.slice(3)}` : s
}

function ProofRow({ p, order, onRemove }: { p: Proof; order: Order; onRemove: () => void }) {
  const item = order.items.find((i) => i.id === p.item_id)
  return (
    <li className="proof">
      <span className="kind">{p.kind_label}</span>
      <div className="body">
        {p.kind === 'url' && <a href={p.url} target="_blank" rel="noopener noreferrer nofollow" className="u">{p.url}</a>}
        {p.kind === 'book' && (
          <div><b>{p.title}</b>{p.author && <> · {p.author}</>}{p.isbn && <span className="mono muted"> · ISBN {isbnHyphens(p.isbn)}</span>}</div>
        )}
        {p.text && <p className="txt">{p.text}</p>}
        {p.file_url && <a className="linkbtn small" href={p.file_url} target="_blank" rel="noreferrer">Datei: {p.file_name} ({kb(p.file_size)})</a>}
        <div className="muted small">
          {item ? `${item.asset.inv} – ${item.asset.title}` : order.items.length > 1 ? 'alle Lizenzen' : ''}
          {(item || order.items.length > 1) && ' · '}
          eingereicht {new Date(p.created_at).toLocaleDateString('de-DE')}
        </div>
      </div>
      {p.reviewed ? <span className="ok small">geprüft</span> : <button className="linkbtn small" onClick={onRemove}>entfernen</button>}
    </li>
  )
}

export function Proofs({ order }: { order: Order }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<ProofKind>('url')
  const [item, setItem] = useState('')
  const [f, setF] = useState(EMPTY)
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const set = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value })
  const update = (o: Order) => qc.setQueryData(['order', order.number], o)

  function pick(fl: File | null) {
    setError(null)
    if (fl && fl.size > MAX_MB * 1024 * 1024) {
      setError(`Die Datei ist zu groß (höchstens ${MAX_MB} MB).`)
      fl = null
    }
    setFile(fl)
    if (!fl && fileInput.current) fileInput.current.value = ''
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    const form = new FormData()
    form.append('kind', kind)
    if (item) form.append('item_id', item)
    const fields: (keyof typeof EMPTY)[] = kind === 'url' ? ['url', 'text'] : kind === 'book' ? ['title', 'author', 'isbn', 'text'] : ['text']
    for (const k of fields) form.append(k, f[k])
    if (file) form.append('file', file)
    try {
      update(await shop.addProof(order.number, form))
      setF(EMPTY)
      pick(null)
      setOpen(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.')
    } finally {
      setBusy(false)
    }
  }

  async function remove(p: Proof) {
    try {
      update(await shop.removeProof(order.number, p.id))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Entfernen fehlgeschlagen.')
    }
  }

  if (order.proofs_blocked && !order.proofs.length) return null
  return (
    <section className="proofs">
      <h2 className="sec">Nachweise</h2>
      <p className="muted small">
        Bitte zeigen Sie uns, wo die Bilder erschienen sind – als Link, Buchangabe oder mit einer Datei (PDF, Foto, Scan).
        Belegexemplare können Sie uns auch per Post schicken.
      </p>
      {!!order.proofs.length && (
        <ul className="proof-list">{order.proofs.map((p) => <ProofRow key={p.id} p={p} order={order} onRemove={() => remove(p)} />)}</ul>
      )}
      {!order.proofs_blocked && !open && (
        <button className="btn" onClick={() => { setOpen(true); setError(null) }}>+ Nachweis hinzufügen</button>
      )}
      {!open && error && <p className="err">{error}</p>}
      {open && (
        <form className="box proof-form" onSubmit={submit}>
          <div className="seg" role="group" aria-label="Art des Nachweises">
            {KINDS.map((k) => (
              <button type="button" key={k.kind} aria-pressed={kind === k.kind} onClick={() => setKind(k.kind)}>{k.label}</button>
            ))}
          </div>
          <div className="fgrid">
            {order.items.length > 1 && (
              <label className="span2">Betrifft
                <select value={item} onChange={(e) => setItem(e.target.value)}>
                  <option value="">alle Lizenzen dieser Bestellung</option>
                  {order.items.map((i) => <option key={i.id} value={i.id}>{i.position}. {i.asset.inv} – {i.asset.title}</option>)}
                </select>
              </label>
            )}
            {kind === 'url' && (
              <label className="span2">Link *<input inputMode="url" autoComplete="url" value={f.url} onChange={set('url')} required placeholder="https://…" autoFocus /></label>
            )}
            {kind === 'book' && (
              <>
                <label className="span2">Titel *<input value={f.title} onChange={set('title')} required autoFocus /></label>
                <label>Autor / Herausgeber<input value={f.author} onChange={set('author')} /></label>
                <label>ISBN<input value={f.isbn} onChange={set('isbn')} inputMode="numeric" placeholder="978-…" /></label>
              </>
            )}
            <label className="span2">{kind === 'other' ? 'Beschreibung' : 'Anmerkung'}
              <textarea rows={kind === 'other' ? 4 : 2} value={f.text} onChange={set('text')}
                placeholder={kind === 'other' ? 'z. B. Ausstellung „…“, Museum X, 1.3.–30.6.2027' : kind === 'book' ? 'z. B. Seite 112, Verlag, Auflage' : ''} />
            </label>
            <div className="span2 file">
              <input ref={fileInput} id="proof-file" type="file" accept={ACCEPT} onChange={(e) => pick(e.target.files?.[0] ?? null)} hidden />
              {file
                ? <span>{file.name} <span className="muted">({kb(file.size)})</span> <button type="button" className="linkbtn small" onClick={() => pick(null)}>entfernen</button></span>
                : <label htmlFor="proof-file" className="linkbtn">Datei anhängen</label>}
              <span className="muted small">PDF oder Bild, bis {MAX_MB} MB{kind === 'other' ? ' – Beschreibung oder Datei genügt' : ' (optional)'}</span>
            </div>
          </div>
          {error && <p className="err">{error}</p>}
          <div className="acts">
            <button className="btn primary" disabled={busy}>{busy ? 'Speichere …' : 'Nachweis speichern'}</button>
            <button type="button" className="linkbtn" onClick={() => { setOpen(false); setError(null) }}>abbrechen</button>
          </div>
        </form>
      )}
    </section>
  )
}
